import { useState } from 'react'
import { Button, Modal, Select } from '@/components/ui'
import type { ConfirmationSource, ShipConfirmationSplit } from '../api/types'

const SOURCE_OPTIONS: { value: ConfirmationSource; label: string }[] = [
  { value: 'vendor_email', label: 'Vendor email' },
  { value: 'vendor_document', label: 'Vendor confirmation document' },
  { value: 'carrier_booking', label: 'Carrier / booking confirmation' },
  { value: 'phone', label: 'Phone / verbal' },
  { value: 'manual', label: 'Manual entry' },
]

export interface ConfirmSplitModalProps {
  split: ShipConfirmationSplit | null
  isPending: boolean
  onCancel: () => void
  onConfirm: (body: { confirmed_date: string; source: ConfirmationSource; detail?: string }) => void
}

/**
 * Confirm (or revise) the vendor's ship date for ONE split shipment.
 * Deliberately simpler than `ConfirmShipDateModal` — there's no "apply
 * to all lines" bulk option here, since confirming a split is already
 * the single-shipment-at-a-time action.
 */
export function ConfirmSplitModal({ split, isPending, onCancel, onConfirm }: ConfirmSplitModalProps) {
  const [confirmedDate, setConfirmedDate] = useState('')
  const [source, setSource] = useState<ConfirmationSource>('vendor_email')
  const [detail, setDetail] = useState('')

  const [prefilledForId, setPrefilledForId] = useState<number | null>(null)
  if (split && split.id !== prefilledForId) {
    setPrefilledForId(split.id)
    setConfirmedDate(split.confirmed_ship_date ?? '')
    setSource((split.confirmation_source as ConfirmationSource | null) ?? 'vendor_email')
    setDetail(split.confirmation_detail ?? '')
  }

  if (!split) return null

  const isRevision = split.stage === 'confirmed'
  const canSubmit = !!confirmedDate

  function reset() {
    setConfirmedDate('')
    setSource('vendor_email')
    setDetail('')
    setPrefilledForId(null)
  }

  return (
    <Modal
      open={split !== null}
      onOpenChange={(next) => {
        if (!next) {
          reset()
          onCancel()
        }
      }}
      tone="info"
      title={isRevision ? `Revise split ${split.split_index} ship date` : `Confirm split ${split.split_index} ship date`}
      description={`Record the vendor's firm ship date for this ${split.quantity}-unit portion of the line.`}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              reset()
              onCancel()
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isPending}
            disabled={!canSubmit}
            onClick={() => onConfirm({ confirmed_date: confirmedDate, source, detail: detail || undefined })}
          >
            {isRevision ? 'Revise ship date' : 'Confirm ship date'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Confirmed ship date</span>
          <input
            type="date"
            value={confirmedDate}
            onChange={(e) => setConfirmedDate(e.target.value)}
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary focus:border-border-focus focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Source</span>
          <Select value={source} onChange={(e) => setSource(e.target.value as ConfirmationSource)}>
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Evidence / notes</span>
          <textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="e.g. booking #, BL number, email subject/date, who confirmed"
            rows={3}
            className="rounded-xs border border-border-default bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
          />
        </label>
      </div>
    </Modal>
  )
}
